/**
 * failedWriteCapture — last-resort durable capture of a field-rep write
 * payload that could NOT be saved.
 *
 * Operator 2026-06-21 (verbatim, after rep Steffen lost 2 Friday visit
 * reports the offline queue never synced): "whenever a visit report cannot be
 * saved MAKE SURE WE GET THE PAYLOAD AS LOGS SOMEWHERE." A report is the
 * product — losing one silently is the worst failure mode. This is the safety
 * net: when a write genuinely fails (online hard-fail, OR an offline-queued
 * critical that exhausted its retries), the FULL payload is captured so it is
 * recoverable, independent of the rep's device.
 *
 * THREE sinks, all best-effort, NONE throws (the safety net must never
 * become the thing that breaks the app):
 *  1. `console.error` — a tagged FULL-payload dump. Captured by the bug-tool
 *     console ring buffer + Sentry breadcrumbs, and survives with NO
 *     connectivity (recoverable from a later submitted bug report — which is
 *     literally how the Steffen loss was first spotted).
 *  2. `scout_activity_log` via `logScoutEvent` — raw fetch + Bearer to the
 *     same durable sink the bug tool uses; works even when the normal/SL write
 *     path is down. Server-side, so the payload is recoverable off-device.
 *     Fire-and-forget; no-ops when offline / logged-out (the console dump is
 *     then the only copy, by design).
 *  3. Sentry `captureException` via the `window.__sentryCapture` bridge — turns
 *     a failed write into an ALERTABLE Sentry ISSUE (not just a passive
 *     breadcrumb or an un-alerted log row), grouped by kind+reason so a lost rep
 *     write PAGES instead of piling up invisibly (operator: never-dark /
 *     proper-errors — 70 `write_save_failed` events sat un-alerted for 2 weeks).
 *     Same bridge constraint as `reportQueryError`: this shared lib can't import
 *     @sentry/react, so it uses the `window.__sentryCapture` bridge main.tsx
 *     installs. The raw `payload` is deliberately NOT sent to Sentry (PII/size)
 *     — it stays in sink 2; Sentry carries only kind/reason/meta for grouping.
 *     Runs LAST so the durable recovery sinks (1 + 2) fire even if it blows up.
 */

import { logScoutEvent, postActivityLog } from '../utils/scoutLog'

/** scout_activity_log event name for a write that could not be persisted. */
export const WRITE_SAVE_FAILED_EVENT = 'write_save_failed'

/** How long we wait for the durable sink to CONFIRM before calling it unconfirmed. */
const DURABLE_CAPTURE_TIMEOUT_MS = 15_000

/**
 * The failure-record KEY, hoisted to the TOP LEVEL of every failed-write event.
 *
 * RULING-EC-VISIT-REPORT-V8-OPEN-RULINGS-2026-10-04 R4: `core.projection_dead_letter` is
 * THE failure record of a visit submit and is keyed by `source_event_id` (uuid); the FE
 * telemetry sink is joined to it on that key. A submit's idempotency key is ALWAYS inside
 * the payload (both visit-report doors carry it), but a key buried in `payload` is not a
 * join column — and the queue layer (enqueue / replay-failure / discard) captures with
 * generic meta that never named it. So the hoist happens HERE, in the one capture
 * primitive every path funnels through, rather than at N call sites that each have to
 * remember it (the class cure, not the instance fix).
 *
 * An explicit `meta.source_event_id` wins (a caller whose payload is the raw answers blob
 * passes the key it minted); otherwise a string `payload.source_event_id` is lifted. A
 * payload without one (non-visit writes) is left exactly as it was — nothing is invented.
 */
export function withSourceEventId(
  payload: unknown,
  meta: Record<string, unknown>,
): Record<string, unknown> {
  if (typeof meta.source_event_id === 'string' && meta.source_event_id !== '') return meta
  if (payload !== null && typeof payload === 'object') {
    const key = (payload as { source_event_id?: unknown }).source_event_id
    if (typeof key === 'string' && key !== '') return { ...meta, source_event_id: key }
  }
  return meta
}

/**
 * CONFIRMED durable capture — the awaited, throws-on-failure sibling of
 * `captureFailedWrite`.
 *
 * `captureFailedWrite` is fire-and-forget by design: it must never break the write
 * path it protects, so it swallows its own sink errors. That makes it perfectly
 * useless as a precondition for DELETING data — "we called the sink" is not "the
 * payload is safe off-device", and acting on the former is how you lose a report.
 *
 * This variant asserts the sink's ACTUAL outcome (`postActivityLog` awaits the
 * insert and throws on a non-2xx), so `discardEntry` can require a REAL landing
 * before it removes a rep's only copy. Resolves when the payload is durably stored
 * off-device; THROWS otherwise — and a throw means DO NOT DELETE.
 */
export async function captureFailedWriteConfirmed(
  kind: string,
  payload: unknown,
  reason: string,
  meta: Record<string, unknown> = {},
): Promise<void> {
  // The console dump first — it is the copy that survives with no connectivity at
  // all, and it costs nothing. It is NOT the confirmation; the server row is.
  captureFailedWrite(kind, payload, reason, meta)

  await postActivityLog(
    WRITE_SAVE_FAILED_EVENT,
    { kind, reason, ...withSourceEventId(payload, meta), payload, durable_capture_confirmed: true },
    { timeoutMs: DURABLE_CAPTURE_TIMEOUT_MS },
  )
}

/**
 * RETAIN-ON-UNCONFIRMED durable capture — the safe wrapper the outbox call-sites
 * use in place of the fire-and-forget `captureFailedWrite`.
 *
 * FR-FE-IRONCLAD-TELEMETRY-OUTBOX. `captureFailedWrite` swallows its own sink
 * errors so it can never break the write path — but that means a session-less
 * rep, a misconfigured client, or a 4xx/5xx from the durable sink vanishes
 * SILENTLY, which is the exact class this FR closes (D-33 R1). This variant:
 *   - routes through `captureFailedWriteConfirmed`, whose `postActivityLog` leg
 *     THROWS on a session-less / misconfigured / non-2xx durable write, and
 *   - NEVER throws itself — it returns a boolean and, on a non-confirm, surfaces
 *     LOUDLY (console.error) instead of returning quietly.
 *
 * The contract the callers rely on: `false` means the payload is NOT confirmed
 * off-device, therefore the on-device entry MUST be RETAINED — a capture that did
 * not land can never justify deleting a rep's only copy. It is safe to leave
 * unawaited (best-effort loudness) or awaited (deterministic, e.g. at enqueue).
 */
export async function captureFailedWriteRetained(
  kind: string,
  payload: unknown,
  reason: string,
  meta: Record<string, unknown> = {},
): Promise<boolean> {
  try {
    await captureFailedWriteConfirmed(kind, payload, reason, meta)
    return true
  } catch (err) {
    // LOUD, not silent (D-33 R1): the durable off-device sink did not confirm. The
    // on-device entry is KEPT by the caller — this is a report we have NOT secured
    // off-device yet, and saying so is the whole point.
    console.error(
      `[failedWriteCapture] durable off-device capture UNCONFIRMED for kind=${kind} `
      + `reason=${reason} — on-device entry RETAINED, nothing deleted`,
      err,
    )
    return false
  }
}

/**
 * The Sentry bridge main.tsx installs on `window` (same shape reportQueryError
 * uses). This lib package cannot import @sentry/react, so an alertable issue is
 * raised through this indirection.
 */
type SentryCapture = (error: unknown, context?: unknown) => void

function errorToString(error: unknown): string {
  if (error == null) return ''
  if (error instanceof Error) return `${error.name}: ${error.message}`
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

/**
 * Capture a write payload that could NOT be saved, to both the console and the
 * durable server-side sink. Never throws.
 *
 * @param kind    the write kind (e.g. a QUEUE_TYPES value like
 *                'visit-report:submit') — so the recovered payload is typed.
 * @param payload the FULL write payload to preserve.
 * @param reason  why it failed (e.g. 'online-submit-error',
 *                'offline-retries-exhausted') — for triage/recovery grouping.
 * @param meta    optional extra context (error string, retries, queue id, …).
 */
export function captureFailedWrite(
  kind: string,
  payload: unknown,
  reason: string,
  rawMeta: Record<string, unknown> = {},
): void {
  // R4: the failure-record key rides top-level on every sink (see withSourceEventId).
  const meta = withSourceEventId(payload, rawMeta)
  // Sink 1 — console. ALWAYS, even with no connectivity, so the payload lands
  // in the bug-tool ring buffer + Sentry breadcrumbs and is recoverable later.
  try {
    let payloadJson: string
    try {
      payloadJson = JSON.stringify(payload)
    } catch {
      payloadJson = '[unserializable payload]'
    }
    console.error(
      `[UNSAVED-WRITE] kind=${kind} reason=${reason} — FULL PAYLOAD (recover this):`,
      payloadJson,
      meta,
    )
  } catch {
    // The safety net must never throw — a console implementation that rejects
    // must not take down the submit/flush path.
  }

  // Sink 2 — durable server-side log (recoverable off-device). Fire-and-forget;
  // logScoutEvent swallows its own errors and no-ops when offline/logged-out.
  logScoutEvent(WRITE_SAVE_FAILED_EVENT, {
    kind,
    reason,
    ...meta,
    payload,
  })

  // Sink 3 — alertable Sentry issue via the window.__sentryCapture bridge, so a
  // failed write PAGES instead of sitting silently in a log. Mirrors
  // reportQueryError's bridge access + guards exactly (same @sentry/react-free
  // constraint). Best-effort, never throws; the raw payload is intentionally
  // omitted (PII/size) — it lives in sink 2, Sentry carries only kind/reason/meta.
  try {
    if (typeof window !== 'undefined') {
      const capture = (window as unknown as { __sentryCapture?: SentryCapture }).__sentryCapture
      if (typeof capture === 'function') {
        const err = new Error(`failed write: ${kind} (${reason})`)
        err.name = 'FailedWriteError'
        capture(err, {
          tags: { source: 'failed-write', kind, reason },
          contexts: { failedWrite: { kind, reason, ...meta } },
        })
      }
    }
  } catch {
    // The safety net must never throw — a bridge that rejects must not take down
    // the submit/flush path (the durable sinks 1 + 2 already fired above).
  }
}

export { errorToString as __errorToString }
